import React, { useState } from 'react';
import Image from 'next/image';
import { formatNumber } from '@/utils/coins/formatNumber';
import ChangeValue from '@/components/(postTypes)/symbols/ChangeValue';
import Link from 'next/link';
import type { Symbol } from '@/types/search/searchTypes';
import dynamic from 'next/dynamic';
import { isUsdQuoted } from '@/utils/coins/quote';

const Sparkline = dynamic(() => import('@/components/(postTypes)/symbols/Sparkline'), {
    ssr: false,
});

interface SymbolCardProps {
    item?: Symbol;
    active?: string;
    action?: () => void;
}

const currencyFaNames: Record<string, string> = {
    usd: 'دلار آمریکا',
    dollar: 'دلار آمریکا',
    eur: 'یورو',
    euro: 'یورو',
    gbp: 'پوند انگلیس',
    pound: 'پوند انگلیس',
    aed: 'درهم امارات',
    derham: 'درهم امارات',
    try: 'لیر ترکیه',
    lira: 'لیر ترکیه',
    jpy: 'ین ژاپن',
    yen: 'ین ژاپن',
    cny: 'یوآن چین',
    yuan: 'یوآن چین',
    cad: 'دلار کانادا',
    aud: 'دلار استرالیا',
    nzd: 'دلار نیوزیلند',
    sgd: 'دلار سنگاپور',
    chf: 'فرانک سوئیس',
    azn: 'منات آذربایجان',
    nok: 'کرون نروژ',
    sek: 'کرون سوئد',
    dkk: 'کرون دانمارک',
    kwd: 'دینار کویت',
    rub: 'روبل روسیه',
    ruble: 'روبل روسیه',
    thb: 'بات تایلند',
    afn: 'افغانی افغانستان',
    inr: 'روپیه هند',
    myr: 'رینگیت مالزی',
    gel: 'لاری گرجستان',
    pkr: 'روپیه پاکستان',
    iqd: 'دینار عراق',
    sar: 'ریال عربستان',
    qar: 'ریال قطر',
    omr: 'ریال عمان',
    geram18: 'گرم طلای ۱۸ عیار',
    '18k': 'طلای ۱۸ عیار',
    '24k': 'طلای ۲۴ عیار',
    sekee: 'سکه امامی',
    sekeb: 'سکه بهار آزادی',
    nim: 'نیم سکه',
    rob: 'ربع سکه',
    gerami: 'سکه گرمی',
    abshodeh: 'مثقال طلای آبشده',
    mesghal: 'مثقال طلا',
    tala: 'طلا',
};

const getSymbolDisplayName = (item?: Symbol) => {
    const slug = (item?.slug || '').toLowerCase();

    // 1. If it's a known currency or gold, use the Persian name
    if (slug && currencyFaNames[slug]) {
        return currencyFaNames[slug];
    }

    // 2. If item has a valid Persian nameFa that is not identical to slug
    if (item?.nameFa && item.nameFa.trim()) {
        const trimmed = item.nameFa.trim();
        if (trimmed.toLowerCase() !== slug) {
            return trimmed;
        }
    }

    const title = item?.title?.trim();
    if (title) {
        // Pattern: "قیمت X امروز | ..." or "قیمت X امروز"
        const mGheymat = title.match(/^قیمت\s+(.+?)\s+امروز(?:\s*[|].*)?$/);
        if (mGheymat && mGheymat[1]) {
            return mGheymat[1].trim();
        }

        // Pattern: "HOT - هولو"
        if (title.includes(' - ')) {
            const parts = title.split(' - ');
            return parts[1]?.trim() || parts[0]?.trim();
        }

        // Pattern: "معرفی ارز دیجیتال X: ..."
        const mMoarefi = title.match(
            /^معرفی\s+(?:ارز دیجیتال|و بررسی(?:\s+جامع)?|استیبل‌?کوین)?\s*([^:：]+)[:：]/,
        );
        if (mMoarefi && mMoarefi[1]) {
            return mMoarefi[1].trim();
        }

        // Pattern: "تحلیلی جامع بر اتنا (Ethena) و استایبل کوین sUSDe"
        const mTahlil = title.match(
            /^تحلیلی?\s+(?:جامع\s+)?بر\s+(?:کریپتوکارنسی\s+)?(.+?)(?:\s+و\s+|\s*[،,:-|]|$)/,
        );
        if (mTahlil && mTahlil[1]) {
            const cand = mTahlil[1].trim();
            if (cand.length < 30) {
                return cand;
            }
        }

        if (title.length <= 25) {
            return title;
        }
    }

    return item?.nameFa?.trim() || (item?.slug ? item.slug.toUpperCase() : '');
};

const SymbolCard = ({ item, active, action }: SymbolCardProps) => {
    const [imageError, setImageError] = useState(false);
    const changeValue = item?.change || 0;
    const sparkline = item?.sparkline;
    let style = '';

    if (changeValue > 0) {
        style = '#0CAB00'; // style for positive change
    } else if (changeValue < 0) {
        style = '#EB0000'; // style for negative change
    } else if (changeValue === 0) {
        style = '#EB0000';
    }

    const displayName = getSymbolDisplayName(item);
    const isFiatOrGold = item?.postType === 'currency' || item?.postType === 'gold';
    // The world gold ounce is quoted in dollars: dollars first, toman beside it
    const usdQuoted = isFiatOrGold && isUsdQuoted(item);
    const isToman = (isFiatOrGold && !usdQuoted) || item?.slug?.toLowerCase() === 'usdt';
    const isSameAsSlug =
        displayName.trim().toLowerCase() === (item?.slug || '').trim().toLowerCase();

    return (
        <Link
            href={item?.uri || '#'}
            onClick={action}
            className={`flex snap-start flex-col lg:w-1/6 transition-all rounded-md border dark:border-white/30 gap-3 hover:bg-gray-50 dark:hover:bg-white/10 ${active === 'true' ? 'w-full lg:w-1/3' : ' w-64'}`}
        >
            <div className='flex items-center gap-2 p-5 pb-0 min-w-0'>
                {item?.iconUrl && !imageError ? (
                    <Image
                        src={item.iconUrl}
                        alt={displayName || 'coin'}
                        title={displayName || 'coin'}
                        width={160}
                        height={160}
                        priority={false}
                        onError={() => setImageError(true)}
                        className={
                            'size-5 rounded-full border-2 border-white dark:border-white/20 bg-white dark:bg-white/10 lg:size-8 object-cover shrink-0'
                        }
                    />
                ) : (
                    <div
                        className={
                            'size-5 rounded-full border-2 border-white dark:border-white/20 bg-primary/10 dark:bg-white/10 text-primary dark:text-white lg:size-8 flex items-center justify-center text-[10px] lg:text-xs font-bold shrink-0'
                        }
                    >
                        {item?.slug ? item.slug.slice(0, 2).toUpperCase() : '?'}
                    </div>
                )}
                <div className={'flex items-center gap-1.5 min-w-0 overflow-hidden'}>
                    <span
                        className={
                            'truncate font-medium text-xs lg:text-sm text-gray-900 dark:text-white'
                        }
                    >
                        {displayName}
                    </span>
                    {item?.slug && !isSameAsSlug && (
                        <span
                            className={
                                'text-xxs text-primary/50 dark:text-white/50 shrink-0 uppercase font-mono'
                            }
                        >
                            {item.slug}
                        </span>
                    )}
                </div>
            </div>
            <div className={'text-right flex gap-2 px-5'}>
                <div className={'inline-flex gap-1 items-center'}>
                    <span className={'text-xs'}>{isToman ? 'تومان' : '$'}</span>
                    {formatNumber(
                        usdQuoted
                            ? item?.price_usd || 0
                            : isFiatOrGold
                              ? item?.usdt_price || item?.price || 0
                              : item?.price || 0,
                        false,
                        true,
                        isToman ? 'TMN' : 'USD',
                        true,
                    )}
                </div>
                {usdQuoted && (
                    <span className='text-xxs text-primary/50 dark:text-white/50 self-center'>
                        {formatNumber(item?.price || 0, false, true, 'TMN', true)} تومان
                    </span>
                )}
                <ChangeValue change={changeValue} className={'text-xs'} />
            </div>
            <div
                dir={'ltr'}
                className={`flex flex-col items-center text-right max-lg:flex max-lg:justify-center font-yekanBakh max-lg:gap-2 lg:items-end ${changeValue > 0 ? 'text-success' : 'text-error'}`}
            >
                {!isNaN(changeValue) && (
                    <div className={'flex h-8 w-full'}>
                        <Sparkline data={sparkline || []} style={style} />
                    </div>
                )}
            </div>
        </Link>
    );
};

export default SymbolCard;
